export type DeviceType = 'ios' | 'android' | 'desktop';

export interface DeviceDetails {
  type: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  userAgent: string;
}

/**
 * Detects the device type based on the user agent string
 * @returns DeviceDetails object containing device information
 */
export function getDeviceDetails(): DeviceDetails {
  // Default fallback for server-side rendering or when navigator is not available
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      type: 'desktop',
      isMobile: false,
      isTablet: false,
      userAgent: '',
    };
  }

  const userAgent = navigator.userAgent.toLowerCase();

  // iOS Detection
  const isiOS = /iphone|ipad|ipod/.test(userAgent) ||
                (userAgent.includes('mac') && 'ontouchend' in document);

  // Android Detection
  const isAndroid = /android/.test(userAgent);

  // Mobile Detection (phones)
  const isMobile = /mobile|phone/.test(userAgent) &&
                   !/tablet|ipad/.test(userAgent);

  // Tablet Detection
  const isTablet = /tablet|ipad/.test(userAgent) ||
                   (isAndroid && !/mobile/.test(userAgent)) ||
                   // iPad on iOS 13+ reports as desktop, so check for touch capability
                   (userAgent.includes('mac') && 'ontouchend' in document);

  let deviceType: DeviceType;

  if (isiOS) {
    deviceType = 'ios';
  } else if (isAndroid) {
    deviceType = 'android';
  } else {
    deviceType = 'desktop';
  }

  return {
    type: deviceType,
    isMobile,
    isTablet,
    userAgent: navigator.userAgent,
  };
}

/**
 * Simple function that just returns the device type string
 * @returns 'ios', 'android', or 'desktop'
 */
export function getDeviceType(): DeviceType {
  return getDeviceDetails().type;
}

/**
 * Check if the current device is mobile (phone)
 * @returns true if device is a mobile phone
 */
export function isMobileDevice(): boolean {
  return getDeviceDetails().isMobile;
}

/**
 * Check if the current device is a tablet
 * @returns true if device is a tablet
 */
export function isTabletDevice(): boolean {
  return getDeviceDetails().isTablet;
}

/**
 * Check if the current device is iOS
 * @returns true if device is iOS (iPhone, iPad, iPod)
 */
export function isIOSDevice(): boolean {
  return getDeviceDetails().type === 'ios';
}

/**
 * Check if the current device is Android
 * @returns true if device is Android
 */
export function isAndroidDevice(): boolean {
  return getDeviceDetails().type === 'android';
}

/**
 * Check if the current device is desktop
 * @returns true if device is desktop/laptop
 */
export function isDesktopDevice(): boolean {
  return getDeviceDetails().type === 'desktop';
}
